import React from 'react';
import { Link } from 'react-router-dom';

function ConclusionFirstGuide({ darkMode }) {
  const cardClass = darkMode 
    ? 'bg-gray-800 text-white' 
    : 'bg-white text-gray-800';

  return (
    <div className="min-h-screen py-8 px-4 bg-gradient-to-br from-orange-50 via-yellow-50 to-red-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="max-w-4xl mx-auto">
        {/* 헤더 */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
            두괄식 답변의 기술
          </h1>
          <p className={`text-xl ${darkMode ? 'text-gray-300' : 'text-gray-600'} leading-relaxed`}>
            동문서답 방지하고 논리적으로 답변하는 완벽한 기술! 🎯
          </p>
        </div>

        {/* 두괄식이 왜 중요한가? */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">🤔</span>
            두괄식이 왜 중요한가?
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50'}`}>
              <h3 className="font-bold mb-3 text-blue-600">🎯 동문서답 방지</h3>
              <p className="text-sm leading-relaxed">
                질문에 정확히 대답하여 면접관이 원하는 답을 들려주고, 시간 낭비 없이 핵심 전달이 가능합니다.
              </p>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-50'}`}>
              <h3 className="font-bold mb-3 text-green-600">🏗️ 구조화된 논리</h3>
              <p className="text-sm leading-relaxed">
                체계적인 답변 구조로 설득력을 높이고, 논리적 사고 능력을 보여줄 수 있습니다.
              </p>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-purple-900 bg-opacity-30' : 'bg-purple-50'}`}>
              <h3 className="font-bold mb-3 text-purple-600">⚡ 즉시 집중력 확보</h3>
              <p className="text-sm leading-relaxed">
                첫 5초 안에 핵심을 전달해 면접관의 집중력을 확보하고 좋은 첫인상을 만듭니다.
              </p>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-orange-900 bg-opacity-30' : 'bg-orange-50'}`}>
              <h3 className="font-bold mb-3 text-orange-600">🎪 기억에 남는 답변</h3>
              <p className="text-sm leading-relaxed">
                명확하고 간결한 답변으로 면접 후에도 기억에 남는 인상을 만들어줍니다.
              </p>
            </div>
          </div>
        </div>

        {/* 핵심 원칙 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">💡</span>
            핵심 원칙
          </h2>
          
          <div className="mb-8">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-yellow-900 bg-opacity-30' : 'bg-yellow-50'} border-l-4 border-yellow-500`}>
              <h3 className="text-xl font-bold mb-4 text-yellow-600">🔑 황금 원칙</h3>
              <p className="text-lg font-semibold mb-4">질문 키워드를 그대로 가져와서 바로 답변하기!</p>
              
              <div className="grid md:grid-cols-2 gap-6 mt-6">
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-red-900 bg-opacity-30' : 'bg-red-50'} border border-red-200`}>
                  <h4 className="font-bold text-red-600 mb-2">❌ 잘못된 방법</h4>
                  <p className="text-sm">"결론부터 말씀드리면..."</p>
                  <p className="text-sm">"음... 그러니까..."</p>
                  <p className="text-sm">"일단 말씀드리면..."</p>
                </div>

                <div className={`p-4 rounded-lg ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-50'} border border-green-200`}>
                  <h4 className="font-bold text-green-600 mb-2">✅ 올바른 방법</h4>
                  <p className="text-sm">질문 키워드 + 내 답변</p>
                  <p className="text-sm">바로 핵심부터 시작</p>
                  <p className="text-sm">동문서답 완전 방지</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 실전 예시들 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">🎭</span>
            실전 예시 (총 40초 답변)
          </h2>
          
          {/* 선택형 질문 */}
          <div className="mb-8">
            <h3 className="text-xl font-bold mb-4 text-center">🔀 선택형 질문</h3>
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border-l-4 border-blue-500`}>
              <div className="space-y-4">
                <div>
                  <span className="font-bold text-blue-600">[질문]</span>
                  <p className="mt-1">"팀워크와 개인역량 중 뭐가 더 중요하다고 생각하세요?"</p>
                </div>
                <div>
                  <span className="font-bold text-green-600">[답변]</span>
                  <p className="mt-1 font-semibold">"팀워크와 개인역량은 모두 중요하지만 그중에서 더 중요한 것은 팀워크입니다."</p>
                  <p className="mt-2 text-sm">그 이유는 첫째, 현대 업무는 혼자 완성하기 어려운 복잡한 프로젝트가 대부분이고, 둘째, 개인 역량이 뛰어나도 소통이 안 되면 성과가 나오지 않으며, 셋째, 팀워크 속에서 개인 역량도 더 빛날 수 있기 때문입니다.</p>
                </div>
              </div>
            </div>
          </div>

          {/* 일반형 질문 */}
          <div className="mb-8">
            <h3 className="text-xl font-bold mb-4 text-center">💬 일반형 질문</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border-l-4 border-purple-500`}>
                <div className="space-y-3">
                  <div>
                    <span className="font-bold text-purple-600">[질문]</span>
                    <p className="mt-1">"우리 회사에 지원한 이유를 말씀해주세요."</p>
                  </div>
                  <div>
                    <span className="font-bold text-green-600">[답변]</span>
                    <p className="mt-1 font-semibold">"네. 제가 ○○기업에 지원한 이유는 ~입니다."</p>
                  </div>
                </div>
              </div>

              <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border-l-4 border-orange-500`}>
                <div className="space-y-3">
                  <div>
                    <span className="font-bold text-orange-600">[질문]</span>
                    <p className="mt-1">"본인의 장점은 뭐라고 생각하세요?"</p>
                  </div>
                  <div>
                    <span className="font-bold text-green-600">[답변]</span>
                    <p className="mt-1 font-semibold">"저의 장점은 ~입니다."</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 경험형 질문 */}
          <div className="mb-6">
            <h3 className="text-xl font-bold mb-4 text-center">📚 경험형 질문</h3>
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border-l-4 border-red-500`}>
              <div className="space-y-4">
                <div>
                  <span className="font-bold text-red-600">[질문]</span>
                  <p className="mt-1">"팀 프로젝트에서 갈등이 있었던 경험과 해결 방법은?"</p>
                </div>
                <div>
                  <span className="font-bold text-green-600">[답변]</span>
                  <p className="mt-1 font-semibold">"팀 프로젝트에서 갈등이 있었던 경험은 대학교 4학년 ○○프로젝트를 했을 때입니다."</p>
                  <p className="mt-2 text-sm">그 때 역할 분담에서 의견 충돌이 있었는데, 이를 해결한 방법은 각자의 강점을 파악해서 적재적소에 배치하는 것이었습니다...</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 기본 구조 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">🏗️</span>
            두괄식 기본 구조 (총 40초)
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className={`p-6 rounded-xl border-l-4 border-blue-500 ${darkMode ? 'bg-blue-900 bg-opacity-20' : 'bg-blue-50'}`}>
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">1</div>
                <span className="font-bold text-blue-600">질문 반복 + 답변</span>
              </div>
              <p className="text-sm font-semibold">5초</p>
              <p className="text-sm mt-2">질문 키워드를 그대로 가져와서 즉시 답변</p>
            </div>

            <div className={`p-6 rounded-xl border-l-4 border-green-500 ${darkMode ? 'bg-green-900 bg-opacity-20' : 'bg-green-50'}`}>
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">2</div>
                <span className="font-bold text-green-600">이유/설명</span>
              </div>
              <p className="text-sm font-semibold">30초</p>
              <p className="text-sm mt-2">구체적인 이유나 경험담을 논리적으로 설명</p>
            </div>

            <div className={`p-6 rounded-xl border-l-4 border-purple-500 ${darkMode ? 'bg-purple-900 bg-opacity-20' : 'bg-purple-50'}`}>
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">3</div>
                <span className="font-bold text-purple-600">마무리</span>
              </div>
              <p className="text-sm font-semibold">5초</p>
              <p className="text-sm mt-2">간단한 정리나 다짐으로 마무리</p>
            </div>
          </div>
        </div>

        {/* 상황별 시작 멘트 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">🎪</span>
            상황별 시작 멘트 패턴
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50'}`}>
              <h3 className="font-bold mb-3 text-blue-600">💭 의견/선택 질문</h3>
              <ul className="space-y-2 text-sm">
                <li>• "저는 ~라고 생각합니다"</li>
                <li>• "~중에서 더 중요한 것은 ~입니다"</li>
                <li>• "제가 ~를 선택하는 이유는"</li>
              </ul>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-50'}`}>
              <h3 className="font-bold mb-3 text-green-600">📚 경험 질문</h3>
              <ul className="space-y-2 text-sm">
                <li>• "~경험은 ~때입니다"</li>
                <li>• "가장 기억에 남는 ~는"</li>
                <li>• "~했던 경험은 ~입니다"</li>
              </ul>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-purple-900 bg-opacity-30' : 'bg-purple-50'}`}>
              <h3 className="font-bold mb-3 text-purple-600">🔍 능력/특성 질문</h3>
              <ul className="space-y-2 text-sm">
                <li>• "저의 ~는 ~입니다"</li>
                <li>• "제가 ~하는 방법은"</li>
                <li>• "~에 대한 제 생각은"</li>
              </ul>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-orange-900 bg-opacity-30' : 'bg-orange-50'}`}>
              <h3 className="font-bold mb-3 text-orange-600">🎯 문제해결 질문</h3>
              <ul className="space-y-2 text-sm">
                <li>• "~의 핵심은 ~라고 봅니다"</li>
                <li>• "~를 해결하는 방법은"</li>
                <li>• "이 상황에서 중요한 것은"</li>
              </ul>
            </div>
          </div>
        </div>

        {/* 피해야 할 실수들 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">🚫</span>
            절대 하면 안 되는 실수들
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-red-900 bg-opacity-30' : 'bg-red-50'} border border-red-200`}>
              <h3 className="font-bold mb-4 text-red-600">❌ 이런 시작은 NO!</h3>
              <ul className="space-y-2 text-sm">
                <li>• <strong>"음..."</strong> - 확신 없어 보임</li>
                <li>• <strong>"결론부터 말씀드리면..."</strong> - 부자연스러움</li>
                <li>• <strong>"그러니까..."</strong> - 논리성 부족</li>
                <li>• <strong>"일단..."</strong> - 임시방편 느낌</li>
                <li>• <strong>"잘 모르겠지만..."</strong> - 자신감 부족</li>
              </ul>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-50'} border border-green-200`}>
              <h3 className="font-bold mb-4 text-green-600">✅ 이런 답변이 좋아요!</h3>
              <ul className="space-y-2 text-sm">
                <li>• <strong>질문 키워드 즉시 반복</strong></li>
                <li>• <strong>명확하고 확신 있는 답변</strong></li>
                <li>• <strong>논리적이고 구조화된 설명</strong></li>
                <li>• <strong>동문서답 완전 방지</strong></li>
                <li>• <strong>자연스러운 한국어 표현</strong></li>
              </ul>
            </div>
          </div>
        </div>

        {/* 실전 연습법 */}
        <div className={`${cardClass} rounded-2xl shadow-2xl p-8 mb-8`}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <span className="text-3xl mr-3">🎯</span>
            실전 연습법
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className={`p-6 rounded-xl ${darkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50'}`}>
              <h3 className="font-bold mb-3 text-blue-600">💬 일상 대화 연습</h3>
              <p className="text-sm leading-relaxed">
                친구나 가족과 대화할 때도 질문을 그대로 반복해서 답변하는 습관을 만들어보세요.
              </p>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-green-900 bg-opacity-30' : 'bg-green-50'}`}>
              <h3 className="font-bold mb-3 text-green-600">📺 뉴스 요약 연습</h3>
              <p className="text-sm leading-relaxed">
                뉴스를 보고 "이 뉴스의 핵심은 ~입니다"라고 30초로 요약해보는 연습을 해보세요.
              </p>
            </div>

            <div className={`p-6 rounded-xl ${darkMode ? 'bg-purple-900 bg-opacity-30' : 'bg-purple-50'}`}>
              <h3 className="font-bold mb-3 text-purple-600">🎬 영화 추천 연습</h3>
              <p className="text-sm leading-relaxed">
                "추천하고 싶은 영화는 ~입니다"처럼 일상적인 추천도 두괄식으로 말해보세요.
              </p>
            </div>
          </div>
        </div>

        {/* 뒤로가기 */}
        <div className="text-center">
          <Link 
            to="/guide-success" 
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            <span className="mr-2">←</span>
            면접성공가이드로 돌아가기
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ConclusionFirstGuide;